import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardActionArea, 
  CardContent, 
  Typography, 
  Grid, 
  Box, 
  Skeleton,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Fade,
  Grow,
  Paper
} from '@mui/material';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import CloudIcon from '@mui/icons-material/Cloud';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

const StyledPage = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
}));

const WelcomeCard = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '20px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '300px',
    height: '300px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-30%',
    left: '-10%',
    width: '200px',
    height: '200px',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '50%',
  }
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(0,0,0,0.05)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  },
}));

const TaskCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.05)',
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    transform: 'translateX(4px)',
  },
}));

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return format(date, "dd/MM/yyyy", { locale: es });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return format(date, "HH:mm", { locale: es });
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: '¡Buenos días!', icon: <WbSunnyIcon sx={{ color: '#FFA726', fontSize: 40 }} /> };
  if (hour < 18) return { text: '¡Buenas tardes!', icon: <CloudIcon sx={{ color: '#42A5F5', fontSize: 40 }} /> };
  return { text: '¡Buenas noches!', icon: <NightsStayIcon sx={{ color: '#7E57C2', fontSize: 40 }} /> };
};

function Home({ employeeName }) {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [todayTasks] = useState([
    { id: 1, title: 'Confirmar evento de Ana Martínez', type: 'event', time: '10:00', completed: false },
    { id: 2, title: 'Revisar inventario de vestidos', type: 'inventory', time: '14:00', completed: true },
    { id: 3, title: 'Seguimiento pago Sr. Rodríguez', type: 'billing', time: '16:30', completed: false },
  ]);
  const [stats, setStats] = useState({
    todayEvents: 0,
    pendingPayments: 0,
    completedTasks: 0
  });

  const navigate = useNavigate();
  const greeting = getGreeting();
  
  const storedEmployee = localStorage.getItem('employee');
  const parsedEmployee = storedEmployee ? JSON.parse(storedEmployee)[0] : null;
  let displayName = employeeName || (parsedEmployee ? parsedEmployee.employeeName : 'Usuario');

  const quickActions = [
    {
      title: 'Nueva Reservación',
      subtitle: 'Agendar evento XV años',
      icon: <CalendarMonthIcon sx={{ fontSize: 32, color: '#f093fb' }} />,
      path: '/events/reservation-form-xv',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      title: 'Facturación',
      subtitle: 'Procesar pagos pendientes',
      icon: <ReceiptIcon sx={{ fontSize: 32, color: '#667eea' }} />,
      path: '/billing/services-billing',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      title: 'Gestionar Clientes',
      subtitle: 'Ver todos los clientes',
      icon: <PeopleAltIcon sx={{ fontSize: 32, color: '#4facfe' }} />,
      path: '/clients/manage-clients',
      gradient: 'linear-gradient(135deg, #306899ff 0%, #00a2aaff 100%)',
    },
    {
      title: 'Inventario',
      subtitle: 'Revisar disponibilidad',
      icon: <InventoryIcon sx={{ fontSize: 32, color: '#43e97b' }} />,
      path: '/inventory/view-items',
      gradient: 'linear-gradient(135deg, #3ac96aff 0%, #2bbba1ff 100%)',
    },
  ];

  const fetchUpcomingEvents = async () => {
    setIsLoading(true);
    let accessToken = localStorage.getItem('token');
    
    try {
      // Agregar timestamp para evitar caché
      const timestamp = new Date().getTime();
      const url = `https://dprincess-training.servehttp.com/events/upcoming-events?t=${timestamp}`;      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error al obtener los eventos: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Verificar si data es un array antes de procesar
      if (Array.isArray(data)) {
        setUpcomingEvents(data.slice(0, 3)); // Solo los primeros 3
        setStats(prev => ({ ...prev, todayEvents: data.length }));
      } else {
        setUpcomingEvents([]);
        setStats(prev => ({ ...prev, todayEvents: 0 }));
      }
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      setUpcomingEvents([]);
      setStats(prev => ({ ...prev, todayEvents: 0 }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Ejecutar después de que el componente se monte
    const token = localStorage.getItem('token');
    
    if (token) {
      fetchUpcomingEvents();
    } else {
      console.warn('useEffect - No token found, skipping fetch');
      setIsLoading(false);
    }
  }, []); // Solo ejecutar una vez al montar el componente

  return (
    <StyledPage>
      {/* Welcome Section */}
      <Fade in timeout={800}>
        <WelcomeCard elevation={0}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {greeting.icon}
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700,
                    fontSize: {
                      xs: '1.5rem',    // 24px en móviles
                      sm: '2rem',      // 32px en tablets
                      md: '2.5rem',    // 40px en desktop
                      lg: '3rem'       // 48px en pantallas grandes
                    }
                  }}>
                    {greeting.text}
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ 
                  fontWeight: 800, 
                  mb: 1,
                  fontSize: {
                    xs: '1.75rem',   // 28px en móviles
                    sm: '2.25rem',   // 36px en tablets
                    md: '2.75rem',   // 44px en desktop
                    lg: '3.5rem'     // 56px en pantallas grandes
                  }
                }}>
                  Bienvenido, {displayName}
                </Typography>
                <Typography variant="h6" sx={{ 
                  opacity: 0.9, 
                  mb: 3,
                  fontSize: {
                    xs: '0.875rem',  // 14px en móviles
                    sm: '1rem',      // 16px en tablets
                    md: '1.125rem',  // 18px en desktop
                    lg: '1.25rem'    // 20px en pantallas grandes
                  }
                }}>
                  Hoy es {format(new Date(), "EEEE, dd 'de' MMMM", { locale: es })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    icon={<EventIcon />} 
                    label={stats.todayEvents > 1 ? `${stats.todayEvents} eventos próximos` : `${stats.todayEvents} evento próximo`} 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                  />
                  {/*<Chip 
                    icon={<CheckCircleIcon />} 
                    label={`${todayTasks.filter(t => t.completed).length}/${todayTasks.length} tareas completadas`} 
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                  />*/}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  width: {
                    xs: 80,        // 80px en móviles
                    sm: 100,       // 100px en tablets
                    md: 120,       // 120px en desktop
                    lg: 140        // 140px en pantallas grandes
                  }, 
                  height: {
                    xs: 80,
                    sm: 100,
                    md: 120,
                    lg: 140
                  }, 
                  mx: 'auto',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  fontSize: {
                    xs: '2rem',    // Tamaño de letra responsivo también
                    sm: '2.5rem',
                    md: '3rem',
                    lg: '3.5rem'
                  },
                  fontWeight: 'bold'
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </Avatar>
            </Grid>
          </Grid>
        </WelcomeCard>
      </Fade>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} lg={8}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            mb: 3, 
            color: '#2D3748',
            fontSize: {
              xs: '1.25rem',   // 20px en móviles
              sm: '1.5rem',    // 24px en tablets
              md: '1.75rem',   // 28px en desktop
              lg: '2rem'       // 32px en pantallas grandes
            }
          }}>
            Acciones Rápidas
          </Typography>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Grow in timeout={600 + index * 100}>
                  <QuickActionCard 
                    onClick={() => navigate(action.path)}
                    sx={{ height: '140px' }}
                  >
                    <CardContent sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      background: action.gradient,
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <Box sx={{ 
                        position: 'absolute', 
                        top: -20, 
                        right: -20, 
                        opacity: 0.1, 
                        transform: 'scale(2)' 
                      }}>
                        {action.icon}
                      </Box>
                      <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Box sx={{ mb: 2 }}>
                          {React.cloneElement(action.icon, { sx: { fontSize: 28 } })}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {action.title}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {action.subtitle}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <ArrowForwardIcon />
                      </Box>
                    </CardContent>
                  </QuickActionCard>
                </Grow>
              </Grid>
            ))}
          </Grid>

          {/* Próximos Eventos */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              mb: 3, 
              color: '#2D3748',
              fontSize: {
                xs: '1.25rem',   // 20px en móviles
                sm: '1.5rem',    // 24px en tablets
                md: '1.75rem',   // 28px en desktop
                lg: '2rem'       // 32px en pantallas grandes
              }
            }}>
              Próximos Eventos
              {/* Botón de debug temporal */}
              <Button 
                size="small" 
                onClick={fetchUpcomingEvents}
                sx={{ ml: 2, fontSize: '0.75rem' }}
                variant="outlined"
              >
                🔄 Recargar
              </Button>
            </Typography>
            {isLoading ? (
              <Box>
                {[...Array(3)].map((_, i) => (
                  <Skeleton
                    key={i}
                    animation="wave"
                    variant="rectangular"
                    height={80}
                    sx={{ mb: 2, borderRadius: '12px' }}
                  />
                ))}
              </Box>
            ) : (
              <Box>
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event, index) => (
                    <Grow in timeout={400 + index * 100} key={event.eventID}>
                      <TaskCard 
                        onClick={() => navigate(`/events/view-event-details/${event.eventID}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <CardContent sx={{ py: 2 }}>
                          <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                              <Avatar sx={{ 
                                bgcolor: '#667eea', 
                                width: 50, 
                                height: 50 
                              }}>
                                <EventIcon />
                              </Avatar>
                            </Grid>
                            <Grid item xs>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {event.eventTitle}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                Cliente: {event.clientName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Chip 
                                  icon={<AccessTimeIcon />}
                                  label={formatDate(event.eventDateStart)}
                                  size="small"
                                  variant="outlined"
                                />
                                <Typography variant="caption" color="textSecondary">
                                  {event.eventLocation}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item>
                              <IconButton>
                                <ArrowForwardIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </TaskCard>
                    </Grow>
                  ))
                ) : (
                  <Paper sx={{ 
                    p: 4, 
                    textAlign: 'center', 
                    borderRadius: '16px',
                    bgcolor: '#f8f9fa'
                  }}>
                    <EventIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, color: '#2D3748' }}>
                      No hay eventos próximos
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      ¡Perfecto momento para planificar nuevos eventos!
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/events/reservation-form-xv')}
                      sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '25px',
                        px: 3
                      }}
                    >
                      Crear Nuevo Evento
                    </Button>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Tareas del Día */}
          {/*
          <Paper sx={{ p: 3, borderRadius: '16px', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#2D3748' }}>
              Tareas de Hoy
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(todayTasks.filter(t => t.completed).length / todayTasks.length) * 100}
              sx={{ mb: 3, borderRadius: '10px', height: 8 }}
            />
            {todayTasks.map((task, index) => (
              <Box 
                key={task.id} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  py: 1.5,
                  borderBottom: index < todayTasks.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                }}
              >
                {task.completed ? (
                  <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                ) : (
                  <PendingIcon sx={{ color: '#FF9800' }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textDecoration: task.completed ? 'line-through' : 'none',
                      color: task.completed ? '#9E9E9E' : '#2D3748'
                    }}
                  >
                    {task.title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {task.time}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
          */}
          {/* Acceso Rápido a Reportes */}
          <Paper sx={{ p: 3, borderRadius: '16px' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#2D3748' }}>
              Reportes y Análisis
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/billing/invoice-report')}
              sx={{ mb: 2, borderRadius: '12px', py: 1.5 }}
              startIcon={<TrendingUpIcon />}
            >
              Ver Reportes de Ingresos
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/events/events-list')}
              sx={{ borderRadius: '12px', py: 1.5 }}
              startIcon={<EventIcon />}
            >
              Gestión de Eventos
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </StyledPage>
  );
}

export default Home;

